import { Button } from "@circleco/compass/components/Button";
import { Link } from "@circleco/compass/components/Link";
import { Typography } from "@circleco/compass/components/Typography";

import { t } from "@/i18n-js/instance";
import type { CurrentCommunity } from "@/react/types/CurrentCommunity";

import { ErrorPageBrandV4 } from "./ErrorPageBrandV4";

interface ErrorPageContentV4Props {
  brandColor?: string;
  communityUrl: string;
  currentCommunity?: CurrentCommunity;
  errorCode: number;
  logoUrl: string;
  onGoToHomepage: () => void;
  onReportError: () => void;
  onTryAgain: () => void;
  title: string;
}

export const ErrorPageContentV4 = ({
  brandColor,
  communityUrl,
  currentCommunity,
  errorCode,
  logoUrl,
  onGoToHomepage,
  onReportError,
  onTryAgain,
  title,
}: ErrorPageContentV4Props) => {
  const isNotFound = errorCode === 404;

  return (
    <div className="flex w-full max-w-2xl flex-col items-center text-center">
      <ErrorPageBrandV4
        brandColor={brandColor}
        communityUrl={communityUrl}
        currentCommunity={currentCommunity}
        logoUrl={logoUrl}
      />

      <Typography
        component="h1"
        variant="heading-lg"
        color="primary"
        className="mt-4"
      >
        {title}
      </Typography>
      <Typography variant="body-md" color="secondary" className="mt-4">
        {t(
          isNotFound
            ? "error_page.v4.page_not_found_description"
            : "error_page.v4.something_went_wrong_description",
        )}
      </Typography>

      <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
        {!isNotFound && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onGoToHomepage}
          >
            {t("error_page.v4.back_to_homepage")}
          </Button>
        )}
        <Button
          type="button"
          variant={isNotFound ? "branded" : "primary"}
          size="sm"
          onClick={isNotFound ? onGoToHomepage : onTryAgain}
        >
          {t(
            isNotFound
              ? "error_page.v4.back_to_homepage"
              : "error_page.try_again",
          )}
        </Button>
      </div>

      <div className="mt-16 flex items-center justify-center gap-2">
        <Typography component="span" variant="body-sm" color="secondary">
          {t("error_page.v4.suspect_bug")}
        </Typography>
        <Link asButton color="branded" size="sm" onClick={onReportError}>
          {t("error_page.v4.report_it")}
        </Link>
      </div>
    </div>
  );
};
